import {
  OakTypography,
  OakTagFunctional,
  OakFlex,
  OakGrid,
  parseColor,
  OakLink,
} from "@oaknational/oak-components";
import styled from "styled-components";

import CardListing, { CardListingProps } from "./CardListing";

export const CardListingStyledLink = styled(OakLink)`
  width: 100%;
  flex-grow: 1;
  &:visited {
    color: ${parseColor("text-link-visited")};
  }
  &:hover {
    text-decoration: none;
    &:not([data-disabled="true"]) {
      .card-listing-header {
        text-decoration: underline;
      }
    }
  }
`;

// Adds a border colour to the flex container which intensifies when the link in the card is hovered, as long as the link is not disabled
export const CardListingStyledFlex = styled(OakFlex)`
  border-color: ${parseColor("border-neutral-lighter")};
  &:has(${CardListingStyledLink}:hover:not([data-disabled="true"])) {
    border-color: ${parseColor("border-neutral-stronger")};
  }
`;

export const CardListingTitle = ({ title }: CardListingProps) => {
  return (
    <OakTypography as="h4" $font={"heading-7"} className="card-listing-header">
      {title}
    </OakTypography>
  );
};

export const CardListingIndex = ({ index }: CardListingProps) => {
  return index ? (
    <OakTypography $font={"heading-7"}>{index}</OakTypography>
  ) : null;
};

export const CardListingCardTags = ({
  tags,
  disabled,
  childCards,
}: CardListingProps) => {
  const childCardTag = childCards?.length ? (
    <OakTagFunctional
      key="options"
      label={`${childCards?.length} options`}
      $background={"bg-primary"}
      $ba={"border-solid-s"}
      $borderRadius={"border-radius-s"}
      $borderColor={"border-neutral"}
      $width={"max-content"}
      useSpan
    />
  ) : null;
  return tags ? (
    <OakFlex
      $flexWrap={"wrap"}
      $width={"100%"}
      $gap={"spacing-8"}
      $pb={"spacing-8"}
    >
      {childCardTag}
      {tags.map((tag) => (
        <OakTagFunctional
          key={tag.label}
          label={tag.label}
          $background={"bg-neutral"}
          isTrailingIcon
          iconName={tag.icon}
          $color={disabled ? "text-disabled" : "text-primary"}
          useSpan
        />
      ))}
    </OakFlex>
  ) : (
    childCardTag
  );
};

export const CardListingSubCopy = ({
  subcopy,
  highlightColorVariant,
}: CardListingProps) => {
  return subcopy ? (
    <OakTypography
      $font={"body-2"}
      $color={
        highlightColorVariant === "secondary" ? "text-inverted" : "text-subdued"
      }
    >
      {subcopy}
    </OakTypography>
  ) : null;
};

export const CardListingLessonCount = ({
  lessonCount,
  highlightColorVariant,
}: CardListingProps) => {
  return lessonCount === undefined ? null : (
    <OakTypography
      $font={"heading-light-7"}
      $width={"max-content"}
      $textWrap={"nowrap"}
      $color={
        highlightColorVariant === "secondary" ? "text-inverted" : "text-subdued"
      }
    >
      {lessonCount + " lesson" + (lessonCount === 1 ? "" : "s")}
    </OakTypography>
  );
};

export const CardListingChildCardList = ({
  childCards,
  disabled,
  layoutVariant,
}: CardListingProps) => {
  if (childCards?.length) {
    return (
      <OakGrid
        $cg={"spacing-20"}
        $rg={"spacing-20"}
        $gridTemplateColumns={
          layoutVariant === "horizontal" ? "repeat(3, 1fr)" : "1fr"
        }
      >
        {childCards.map((child) => (
          <CardListing
            {...child}
            layoutVariant="vertical"
            key={child.title}
            showBorder
            disabled={disabled}
          />
        ))}
      </OakGrid>
    );
  } else {
    return null;
  }
};
